import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/app/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name || user.email}`);
      navigate(user.onboarding_completed ? from : "/app/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="flex items-center gap-2.5 mb-8 justify-center" data-testid="login-back-home">
          <span className="w-10 h-10 rounded-full bg-[#4A6148] flex items-center justify-center text-white font-wordmark tracking-tight text-xs font-bold leading-none">WYB</span>
          <span className="font-wordmark text-[#2C302E] text-lg leading-none">What&rsquo;s Your Balance<span className="font-wordmark-italic text-[#4A6148]">?</span></span>
        </Link>
        <div className="card-soft p-8">
          <h1 className="font-heading text-2xl font-medium text-[#2C302E]">Welcome back</h1>
          <p className="text-sm text-[#59605C] mt-1">Log in to see this month’s forecast.</p>

          <button
            type="button"
            onClick={googleLogin}
            data-testid="login-google-button"
            className="mt-6 w-full btn-secondary"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-[#8A918D]">
            <div className="h-px flex-1 bg-[#EBE6E0]" /> or use email <div className="h-px flex-1 bg-[#EBE6E0]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#59605C] mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-soft"
                data-testid="login-email-input"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#59605C]">Password</label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-[#4A6148] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-soft"
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div className="rounded-xl bg-[#FDF2F0] border border-[#F5D8D4] text-[#8C3A30] text-sm p-3" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit-button"
            >
              {loading ? "Logging in…" : "Log in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-center text-[#59605C]">
            New here?{" "}
            <Link to="/signup" data-testid="login-to-signup" className="text-[#4A6148] font-medium hover:underline">
              Start free trial
            </Link>
          </div>
        </div>

        <div className="mt-6 text-xs text-center text-[#8A918D]">
          Try the demo: <span className="text-[#59605C]">demo@monthlycash.app</span> · <span className="text-[#59605C]">demo123!</span>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.5 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 11.5-4.9 11.5-11.7 0-.8-.1-1.4-.2-2H12z" />
    </svg>
  );
}
